// İşler/Tamamlanan satır aksiyonları — Geçmiş Rapor Aktarımı (Task 13).
//
// Eng review (2026-09-24) + tasarım review 12A/18A:
//   · İptal   — `uploaded…writing` (ConfirmDialog; 409 → `cancelRaced`)
//   · Yeniden dene — `failed` ∧ `failureReason !== "llm_auth"`; llm_auth'ta PASİF
//                    + `retryDisabledAuth` title (sunucu da 409 döner)
//   · Geri çek — YALNIZ `done` ∧ `mode === "create"` (ConfirmDialog)
//   · Projeyi aç / Raporu aç (`projects.import_report` izniyle) / eşlenmemiş (n)
// `JobsTab` yalnız tetikler; onay diyaloğunu o yönetir (tek diyalog, satır başına değil).

import { Link } from "react-router-dom";
import type { ReportImport } from "@mahir/shared";
import { fillTemplate } from "../../lib/period-ui";
import {
  IMPORT_COPY,
  fileOpenUrl,
  importFileUrl,
  primaryImportFile,
} from "../../lib/report-import-ui";

const CANCELLABLE: ReportImport["status"][] = [
  "uploaded",
  "queued",
  "mapping",
  "extracting",
  "writing",
];

export interface JobsRowActionsProps {
  row: ReportImport;
  canOpenReport: boolean;
  busyId: string | null;
  onCancel: (row: ReportImport) => void;
  onRetry: (row: ReportImport) => void;
  onArchive: (row: ReportImport) => void;
  onUnmapped: (row: ReportImport) => void;
}

const btn: React.CSSProperties = {
  padding: "3px 10px",
  borderRadius: 6,
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  color: "var(--color-text)",
  fontSize: "0.78rem",
  cursor: "pointer",
};

const link: React.CSSProperties = {
  color: "var(--color-primary)",
  textDecoration: "underline",
  fontWeight: 600,
  fontSize: "0.875rem",
};

export function JobsRowActions({
  row,
  canOpenReport,
  busyId,
  onCancel,
  onRetry,
  onArchive,
  onUnmapped,
}: JobsRowActionsProps): JSX.Element {
  const busy = busyId === row.id;
  const primary = primaryImportFile(row.files);
  const retryBlocked = row.failureReason === "llm_auth";

  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      {CANCELLABLE.includes(row.status) ? (
        <button type="button" style={btn} disabled={busy} onClick={() => onCancel(row)}>
          {busy ? "iptal ediliyor…" : IMPORT_COPY.actionCancel}
        </button>
      ) : null}

      {row.status === "failed" ? (
        <button
          type="button"
          style={{ ...btn, opacity: retryBlocked ? 0.5 : 1 }}
          disabled={retryBlocked || busy}
          title={retryBlocked ? IMPORT_COPY.retryDisabledAuth : undefined}
          onClick={() => onRetry(row)}
        >
          {IMPORT_COPY.actionRetry}
        </button>
      ) : null}

      {row.projectId ? (
        <Link to={`/projects/${row.projectId}`} style={link}>
          {IMPORT_COPY.actionOpenProject}
        </Link>
      ) : null}

      {canOpenReport && primary ? (
        <a
          href={fileOpenUrl(importFileUrl(primary), primary.kind)}
          target="_blank"
          rel="noopener noreferrer"
          style={link}
        >
          {IMPORT_COPY.actionOpenReport}
        </a>
      ) : null}

      {row.unmappedCount > 0 ? (
        <button type="button" style={btn} onClick={() => onUnmapped(row)}>
          {fillTemplate(IMPORT_COPY.unmappedCount, { n: row.unmappedCount })}
        </button>
      ) : null}

      {row.status === "done" && row.mode === "create" ? (
        <button type="button" style={btn} disabled={busy} onClick={() => onArchive(row)}>
          {IMPORT_COPY.actionArchive}
        </button>
      ) : null}
    </div>
  );
}
